import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AdminService, Relative, PendingRelativesResponse, ApproveRelativeRequest } from '../../services/admin.service';

@Component({
  selector: 'app-approve-relatives',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule],
  template: `
    <div class="approve-relatives-container">
      <!-- Header -->
      <div class="page-header">
        <div class="header-content">
          <h1>Approve Relatives</h1>
          <p>Review and approve pending family member registrations</p>
        </div>
        <div class="header-actions">
          <button class="btn-primary" (click)="approveSelected()" [disabled]="selectedRelatives.length === 0 || isLoading">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 11.08V12A10 10 0 1 1 5.93 5.93" stroke="currentColor" stroke-width="2"/>
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2"/>
            </svg>
            Approve Selected ({{ selectedRelatives.length }})
          </button>
        </div>
      </div>

      <!-- Search and Filter Section -->
      <div class="search-section">
        <div class="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
            <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2"/>
          </svg>
          <input 
            type="text" 
            placeholder="Search pending relatives by name, email, or referral code..." 
            [(ngModel)]="searchTerm" 
            (input)="filterRelatives()"
            class="search-input">
        </div>
        <div class="filter-actions">
          <button class="btn-secondary" (click)="loadPendingRelatives()">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <polyline points="23,4 23,10 17,10" stroke="currentColor" stroke-width="2"/>
              <polyline points="1,20 1,14 7,14" stroke="currentColor" stroke-width="2"/>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15" stroke="currentColor" stroke-width="2"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      <!-- Pending Relatives Table -->
      <div class="table-container">
        <div class="table-responsive" *ngIf="pendingRelatives && pendingRelatives.length > 0">
          <table class="relatives-table">
            <thead>
              <tr>
                <th>
                  <input type="checkbox" 
                         [checked]="isAllSelected()" 
                         (change)="toggleSelectAll()"
                         class="checkbox-input">
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Gender</th>
                <th>Date of Birth</th>
                <th>Referral Code</th>
                <th>Parent Code</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let relative of pendingRelatives" [class.selected]="isSelected(relative.id)">
                <td>
                  <input type="checkbox" 
                         [checked]="isSelected(relative.id)" 
                         (change)="toggleSelect(relative.id)"
                         class="checkbox-input">
                </td>
                <td>
                  <div class="relative-name">
                    <strong>{{ relative.firstName }} {{ relative.lastName }}</strong>
                    <span *ngIf="relative.middleName" class="middle-name">{{ relative.middleName }}</span>
                    <span *ngIf="relative.isDeceased" class="status-badge deceased">Deceased</span>
                  </div>
                </td>
                <td>{{ relative.email }}</td>
                <td>{{ relative.mobileNumber }}</td>
                <td>{{ relative.gender }}</td>
                <td>{{ formatDate(relative.dateOfBirth) }}</td>
                <td>
                  <code class="referral-code">{{ relative.referralCode }}</code>
                </td>
                <td>
                  <span *ngIf="relative.parentReferralCode" class="parent-ref">
                    {{ relative.parentReferralCode }}
                  </span>
                  <span *ngIf="!relative.parentReferralCode" class="no-parent">-</span>
                </td>
                <td>{{ formatDate(relative.createdAt) }}</td>
                <td>
                  <div class="action-buttons">
                    <button class="btn-icon approve" (click)="approveSingle(relative)" title="Approve">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 11.08V12A10 10 0 1 1 5.93 5.93" stroke="currentColor" stroke-width="2"/>
                        <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2"/>
                      </svg>
                    </button>
                    <button class="btn-icon view" (click)="viewDetails(relative)" title="View Details">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" stroke-width="2"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div *ngIf="!pendingRelatives || pendingRelatives.length === 0" class="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 11.08V12A10 10 0 1 1 5.93 5.93" stroke="currentColor" stroke-width="2"/>
            <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2"/>
          </svg>
          <h3>No pending relatives</h3>
          <p *ngIf="searchTerm">No pending relatives match your search criteria.</p>
          <p *ngIf="!searchTerm">All relatives have been approved. Great job!</p>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-container" *ngIf="relativesData && relativesData.totalPages > 1">
        <div class="pagination-info">
          <p>
            Showing {{ (relativesData.page - 1) * relativesData.pageSize + 1 }} to 
            {{ Math.min(relativesData.page * relativesData.pageSize, relativesData.totalCount) }} 
            of {{ relativesData.totalCount }} entries
          </p>
          <p *ngIf="relativesData.pendingCount !== undefined" class="pending-count">
            <strong>{{ relativesData.pendingCount }} pending approval</strong>
          </p>
        </div>
        <div class="pagination-controls">
          <button 
            class="btn-pagination" 
            [disabled]="relativesData.page <= 1"
            (click)="loadPendingRelatives(relativesData.page - 1)">
            Previous
          </button>
          <span class="page-info">Page {{ relativesData.page }} of {{ relativesData.totalPages }}</span>
          <button 
            class="btn-pagination" 
            [disabled]="relativesData.page >= relativesData.totalPages"
            (click)="loadPendingRelatives(relativesData.page + 1)">
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- Approval Modal -->
    <div class="modal-overlay" *ngIf="showApprovalModal" (click)="closeApprovalModal()">
      <div class="modal-content" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h2>Approve Relatives</h2>
          <button class="modal-close" (click)="closeApprovalModal()">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/>
              <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </div>
        
        <form [formGroup]="approvalForm" (ngSubmit)="confirmApproval()" class="modal-form">
          <div class="approval-summary">
            <p>You are about to approve <strong>{{ selectedRelatives.length }}</strong> relative(s):</p>
            <ul class="selected-list">
              <li *ngFor="let relative of getSelectedRelatives()">
                {{ relative.firstName }} {{ relative.lastName }} ({{ relative.email }})
              </li>
            </ul>
          </div>

          <div class="form-group">
            <label for="approvedBy">Approved By *</label>
            <input 
              type="text" 
              id="approvedBy"
              formControlName="approvedBy" 
              class="form-input"
              placeholder="Enter your name">
          </div>

          <div class="form-group">
            <label for="approvalNotes">Approval Notes (Optional)</label>
            <textarea 
              id="approvalNotes"
              formControlName="approvalNotes" 
              class="form-textarea"
              placeholder="Add any notes about the approval..."
              rows="3"></textarea>
          </div>

          <div class="modal-actions">
            <button type="button" class="btn-secondary" (click)="closeApprovalModal()">Cancel</button>
            <button type="submit" class="btn-primary" [disabled]="approvalForm.invalid || isLoading">
              <span *ngIf="isLoading">Approving...</span>
              <span *ngIf="!isLoading">Approve {{ selectedRelatives.length }} Relative(s)</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  styles: [`
    /* Approve Relatives Styles */
    .approve-relatives-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
      padding: 2rem;
    }

    /* Header */
    .page-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 2rem;
      border-radius: 16px;
      margin-bottom: 2rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    }

    .header-content h1 {
      margin: 0;
      font-size: 2.5rem;
      font-weight: 700;
    }

    .header-content p {
      margin: 0.5rem 0 0 0;
      opacity: 0.9;
      font-size: 1.1rem;
    }

    .header-actions .btn-primary {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.3);
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: 12px;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 500;
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .header-actions .btn-primary:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }

    .header-actions .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Search Section */
    .search-section {
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      margin-bottom: 2rem;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
    }

    .search-box {
      position: relative;
      flex: 1;
      max-width: 500px;
    }

    .search-box svg {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #64748b;
    }

    .search-input {
      width: 100%;
      padding: 0.75rem 1rem 0.75rem 2.5rem;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      font-size: 1rem;
      transition: all 0.3s ease;
    }

    .search-input:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    .filter-actions .btn-secondary {
      background: #f1f5f9;
      color: #64748b;
      border: 1px solid #e2e8f0;
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .filter-actions .btn-secondary:hover {
      background: #e2e8f0;
      transform: translateY(-1px);
    }

    /* Table Container */
    .table-container {
      background: white;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      margin-bottom: 2rem;
    }

    .table-responsive {
      overflow-x: auto;
    }

    .relatives-table {
      width: 100%;
      border-collapse: collapse;
    }

    .relatives-table th {
      background: #f8fafc;
      padding: 1rem;
      text-align: left;
      font-weight: 600;
      color: #1e293b;
      border-bottom: 1px solid #e2e8f0;
    }

    .relatives-table td {
      padding: 1rem;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: middle;
    }

    .relatives-table tbody tr:hover {
      background: #f8fafc;
    }

    .relatives-table tbody tr.selected {
      background: #f7f5ff;
      border-left: 4px solid #667eea;
    }

    .checkbox-input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    .relative-name strong {
      color: #1e293b;
    }

    .middle-name {
      color: #64748b;
      font-size: 0.9rem;
      margin-left: 0.5rem;
    }

    .status-badge {
      display: inline-block;
      padding: 0.25rem 0.5rem;
      border-radius: 6px;
      font-size: 0.8rem;
      font-weight: 500;
      margin-left: 0.5rem;
    }

    .status-badge.deceased {
      background: #f3f4f6;
      color: #374151;
    }

    .referral-code {
      background: #f1f5f9;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.8rem;
      color: #1e293b;
      font-family: monospace;
    }

    .parent-ref {
      color: #667eea;
      font-weight: 500;
      font-family: monospace;
    }

    .no-parent {
      color: #94a3b8;
      font-style: italic;
    }

    .action-buttons {
      display: flex;
      gap: 0.5rem;
    }

    .btn-icon {
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      padding: 0.5rem;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .btn-icon:hover {
      background: #e2e8f0;
      transform: translateY(-1px);
    }

    .btn-icon.approve:hover {
      background: #e9d5ff;
      border-color: #d8b4fe;
      color: #667eea;
    }

    .btn-icon.view:hover {
      background: #dbeafe;
      border-color: #bfdbfe;
      color: #1e40af;
    }

    /* Empty State */
    .empty-state {
      text-align: center;
      padding: 3rem;
      color: #64748b;
    }

    .empty-state svg {
      margin-bottom: 1rem;
      opacity: 0.5;
    }

    .empty-state h3 {
      margin: 0 0 0.5rem 0;
      color: #1e293b;
    }

    .empty-state p {
      margin: 0;
    }

    /* Pagination */
    .pagination-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: white;
      padding: 1.5rem;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    }

    .pagination-info p {
      margin: 0;
      color: #64748b;
    }

    .pending-count {
      color: #667eea !important;
      font-weight: 600;
    }

    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .btn-pagination {
      background: #f1f5f9;
      color: #64748b;
      border: 1px solid #e2e8f0;
      padding: 0.5rem 1rem;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .btn-pagination:hover:not(:disabled) {
      background: #e2e8f0;
    }

    .btn-pagination:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .page-info {
      color: #64748b;
      font-weight: 500;
    }

    /* Modal Styles */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 2rem;
    }

    .modal-content {
      background: white;
      border-radius: 16px;
      width: 100%;
      max-width: 500px;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
    }

    .modal-header {
      padding: 1.5rem 2rem;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .modal-header h2 {
      margin: 0;
      color: #1e293b;
      font-size: 1.5rem;
      font-weight: 600;
    }

    .modal-close {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 8px;
      transition: all 0.3s ease;
    }

    .modal-close:hover {
      background: #f1f5f9;
    }

    .modal-form {
      padding: 2rem;
    }

    .approval-summary {
      background: #f7f5ff;
      border: 1px solid #e9d5ff;
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 1.5rem;
    }

    .approval-summary p {
      margin: 0 0 0.5rem 0;
      color: #667eea;
      font-weight: 500;
    }

    .selected-list {
      margin: 0.5rem 0 0 0;
      padding-left: 1.5rem;
      color: #667eea;
    }

    .selected-list li {
      margin-bottom: 0.25rem;
    }

    .form-group {
      margin-bottom: 1.5rem;
    }

    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      color: #374151;
      font-weight: 500;
    }

    .form-input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 1rem;
      transition: all 0.3s ease;
    }

    .form-input:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    .form-textarea {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 1rem;
      transition: all 0.3s ease;
      resize: vertical;
      min-height: 80px;
    }

    .form-textarea:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      padding-top: 1.5rem;
      border-top: 1px solid #e2e8f0;
    }

    .modal-actions .btn-primary {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .modal-actions .btn-primary:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
    }

    .modal-actions .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .modal-actions .btn-secondary {
      background: #f1f5f9;
      color: #64748b;
      border: 1px solid #e2e8f0;
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .modal-actions .btn-secondary:hover {
      background: #e2e8f0;
    }

    /* Responsive Design */
    @media (max-width: 768px) {
      .approve-relatives-container {
        padding: 1rem;
      }

      .page-header {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
      }

      .search-section {
        flex-direction: column;
        align-items: stretch;
      }

      .pagination-container {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
      }

      .modal-overlay {
        padding: 1rem;
      }

      .modal-content {
        max-height: 95vh;
      }

      .modal-form {
        padding: 1rem;
      }
    }
  `]
})
export class ApproveRelativesComponent implements OnInit {
  pendingRelatives: Relative[] = [];
  relativesData: PendingRelativesResponse | null = null;
  searchTerm = '';
  isLoading = false;
  Math = Math;

  // Selection state
  selectedRelatives: number[] = [];

  // Modal state
  showApprovalModal = false;
  approvalForm!: FormGroup;

  constructor(
    public authService: AuthService,
    private adminService: AdminService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.loadPendingRelatives();
    this.initializeForm();
  }

  initializeForm(): void {
    this.approvalForm = this.fb.group({
      approvedBy: ['', [Validators.required, Validators.minLength(2)]],
      approvalNotes: ['']
    });
  }

  loadPendingRelatives(page: number = 1): void {
    this.isLoading = true;
    this.adminService.getPendingRelatives(page, 20, this.searchTerm || undefined).subscribe({
      next: (data) => {
        this.pendingRelatives = data.relatives;
        this.relativesData = data;
        this.selectedRelatives = []; // Clear selection on new page
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading pending relatives:', error);
        this.isLoading = false;
      }
    });
  }

  filterRelatives(): void {
    // Use server-side search
    this.loadPendingRelatives(1);
  }

  // Selection methods
  isSelected(relativeId: number): boolean {
    return this.selectedRelatives.includes(relativeId);
  }

  toggleSelect(relativeId: number): void {
    if (this.isSelected(relativeId)) {
      this.selectedRelatives = this.selectedRelatives.filter(id => id !== relativeId);
    } else {
      this.selectedRelatives.push(relativeId);
    }
  }

  isAllSelected(): boolean {
    return this.pendingRelatives.length > 0 && this.selectedRelatives.length === this.pendingRelatives.length;
  }

  toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.selectedRelatives = [];
    } else {
      this.selectedRelatives = this.pendingRelatives.map(r => r.id);
    }
  }

  getSelectedRelatives(): Relative[] {
    return this.pendingRelatives.filter(r => this.selectedRelatives.includes(r.id));
  }

  // Approval methods
  approveSelected(): void {
    if (this.selectedRelatives.length === 0) return;
    this.showApprovalModal = true;
  }

  approveSingle(relative: Relative): void {
    this.selectedRelatives = [relative.id];
    this.showApprovalModal = true;
  }

  confirmApproval(): void {
    if (this.approvalForm.valid && this.selectedRelatives.length > 0) {
      this.isLoading = true;
      const formData = this.approvalForm.value;
      
      const requests: ApproveRelativeRequest[] = this.selectedRelatives.map(id => ({
        relativeId: id,
        approvedBy: formData.approvedBy,
        approvalNotes: formData.approvalNotes
      }));

      this.adminService.approveRelativesBatch(requests).subscribe({
        next: (response) => {
          console.log('Relatives approved:', response);
          this.closeApprovalModal();
          this.loadPendingRelatives();
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error approving relatives:', error);
          this.isLoading = false;
        }
      });
    }
  }

  viewDetails(relative: Relative): void {
    // TODO: Implement detailed view modal
    console.log('Viewing details for relative:', relative);
  }

  closeApprovalModal(): void {
    this.showApprovalModal = false;
    this.approvalForm.reset();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }
}